"use client";
import React, { useMemo, useState, useEffect } from "react";
import Link from "next/link";
import { useSelector } from "react-redux";
import {
  FaArrowRight,
  FaCar,
  FaEnvelope,
  FaMagnifyingGlass,
  FaPhone,
  FaRoad,
  FaUserTie,
} from "react-icons/fa6";
import { BsFillFuelPumpFill } from "react-icons/bs";
import { TbManualGearboxFilled } from "react-icons/tb";
import { IoMdSpeedometer } from "react-icons/io";
import { FaCalendarAlt } from "react-icons/fa";
import { displayCarBuys } from "../../../feature/reducers/carBuySlice";
import { ICarBuy } from "../../../interface";

const NO_CARS: ICarBuy[] = [];
const CAR_CATEGORIES = [
  "PKW",
  "Transporter",
  "Wohnmobil",
  "Wohnwagen",
  "LKW",
  "Motorrad",
  "Oldtimer",
  "Sonstiges",
];

type Sort = "new" | "priceAsc" | "priceDesc" | "kmAsc";

const toNumber = (v: unknown) => {
  const n = Number(
    String(v ?? "")
      .replace(/[^\d.,]/g, "")
      .replace(/\./g, "")
      .replace(",", "."),
  );
  return Number.isFinite(n) && String(v ?? "").trim() !== "" ? n : null;
};

const formatPrice = (v: unknown) => {
  const n = toNumber(v);
  return n !== null ? `${n.toLocaleString("de-DE")} €` : null;
};

const formatKm = (v: unknown) => {
  const n = toNumber(v);
  return n !== null ? `${n.toLocaleString("de-DE")} km` : v ? String(v) : null;
};

// Erstzulassung als "03/2019"
const formatRegistration = (v: unknown) => {
  if (!v) return null;
  const d = new Date(String(v));
  if (isNaN(d.getTime())) return String(v);
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const formatPower = (v: unknown) => {
  if (!v) return null;
  const s = String(v).trim();
  return /^\d+$/.test(s) ? `${s} PS` : s;
};

const Page = () => {
  const storeCars = useSelector(displayCarBuys) as ICarBuy[];
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Server und erstes Rendern im Browser zeigen dasselbe (leer) → kein Hydration-Fehler
  const cars = mounted ? storeCars : NO_CARS;
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("Alle");
  const [sort, setSort] = useState<Sort>("new");

  // Nur Kategorien anbieten, in denen es auch Autos gibt
  const categoriesWithCars = useMemo(
    () =>
      CAR_CATEGORIES.filter((cat) =>
        cars.some((car) => car?.carCategory === cat),
      ),
    [cars],
  );

  const visibleCars = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = cars.filter((car) => {
      const matchesCategory =
        category === "Alle" || car?.carCategory === category;
      const matchesSearch =
        !term ||
        car?.carTitle?.toLowerCase().includes(term) ||
        car?.carIdentificationNumber?.toLowerCase().includes(term);
      return matchesCategory && matchesSearch;
    });

    const byNumber =
      (field: keyof ICarBuy, dir: 1 | -1) => (a: ICarBuy, b: ICarBuy) =>
        ((toNumber(a[field]) ?? Infinity) - (toNumber(b[field]) ?? Infinity)) *
        dir;

    switch (sort) {
      case "priceAsc":
        return [...filtered].sort(byNumber("carPrice", 1));
      case "priceDesc":
        return [...filtered].sort(
          (a, b) => (toNumber(b.carPrice) ?? -1) - (toNumber(a.carPrice) ?? -1),
        );
      case "kmAsc":
        return [...filtered].sort(byNumber("carKilometers", 1));
      default:
        return [...filtered].sort(
          (a: any, b: any) =>
            new Date(b.createdAt || 0).getTime() -
            new Date(a.createdAt || 0).getTime(),
        );
    }
  }, [cars, category, searchTerm, sort]);

  const resetFilters = () => {
    setSearchTerm("");
    setCategory("Alle");
  };

  return (
    <section className="bg-ao-bg text-ao-fg">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-8 px-5 py-10 lg:gap-10 lg:px-20 lg:py-16">
        {/* Kopf */}
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <div className="flex flex-col gap-3">
            <span className="text-xs font-semibold tracking-[0.18em] text-ao-accent-fg lg:text-[13px]">
              AN- &amp; VERKAUF
            </span>
            <h1 className="font-display text-4xl font-extrabold uppercase leading-none lg:text-6xl">
              Fahrzeuge im Bestand
            </h1>
          </div>
          {cars.length > 0 && (
            <p className="text-[15px] text-ao-muted">
              {visibleCars.length} von {cars.length} Fahrzeugen
            </p>
          )}
        </div>

        {/* Suche & Filter – nur wenn es Autos gibt */}
        {cars.length > 0 && (
          <div className="flex flex-col gap-4 rounded-2xl border border-ao-line bg-ao-surface p-4 lg:p-5">
            <div className="flex flex-col gap-3 md:flex-row">
              <label className="relative flex-1">
                <span className="sr-only">Suchen</span>
                <FaMagnifyingGlass className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ao-muted" />
                <input
                  type="search"
                  placeholder="Nach Modell oder Fahrzeug-ID suchen …"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-12 w-full rounded-[10px] border border-ao-line-strong bg-ao-bg pl-11 pr-4 text-[15px] text-ao-fg placeholder:text-ao-muted focus:border-ao-accent focus:outline-none"
                />
              </label>
              <label className="md:w-56">
                <span className="sr-only">Sortieren</span>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as Sort)}
                  className="h-12 w-full rounded-[10px] border border-ao-line-strong bg-ao-bg px-4 text-[15px] text-ao-fg focus:border-ao-accent focus:outline-none"
                >
                  <option value="new">Neueste zuerst</option>
                  <option value="priceAsc">Preis aufsteigend</option>
                  <option value="priceDesc">Preis absteigend</option>
                  <option value="kmAsc">Kilometer aufsteigend</option>
                </select>
              </label>
            </div>

            {categoriesWithCars.length > 1 && (
              <div
                className="-mx-4 flex gap-2 overflow-x-auto px-4 lg:mx-0 lg:flex-wrap lg:px-0"
                role="group"
                aria-label="Kategorie"
              >
                {["Alle", ...categoriesWithCars].map((cat) => {
                  const active = category === cat;
                  return (
                    <button
                      key={cat}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setCategory(cat)}
                      className={`h-10 shrink-0 rounded-full px-4 text-sm font-semibold transition-colors ${
                        active
                          ? "bg-ao-accent text-ao-on-accent"
                          : "border border-ao-line-strong text-ao-soft hover:border-ao-accent"
                      }`}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Liste */}
        {visibleCars.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-5">
            {visibleCars.map((car) => {
              const price = formatPrice(car?.carPrice);
              const specs = [
                { icon: <FaRoad />, value: formatKm(car?.carKilometers) },
                {
                  icon: <FaCalendarAlt />,
                  value: formatRegistration(car?.carFirstRegistrationDay),
                },
                {
                  icon: <IoMdSpeedometer />,
                  value: formatPower(car?.carHorsePower),
                },
                { icon: <BsFillFuelPumpFill />, value: car?.fuelType },
                { icon: <TbManualGearboxFilled />, value: car?.carGearbox },
                {
                  icon: <FaUserTie />,
                  value: car?.owner ? `${car.owner}. Hand` : null,
                },
              ].filter((s) => s.value);

              return (
                <Link
                  key={car?._id}
                  href={`/fahrzeugkaufen/${car?._id}`}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-ao-line bg-ao-surface transition-colors hover:border-ao-accent"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-ao-surface-2">
                    {car?.carImages?.[0] ? (
                      <img
                        src={car.carImages[0]}
                        alt={car.carTitle || "Fahrzeug"}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-5xl text-ao-line-strong">
                        <FaCar />
                      </div>
                    )}
                    {car?.carCategory && (
                      <span className="absolute left-3 top-3 rounded-full bg-ao-bg/85 px-3 py-1 text-xs font-bold backdrop-blur">
                        {car.carCategory}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-1 flex-col gap-4 p-6">
                    <div className="flex flex-col gap-1">
                      <h2 className="font-display text-2xl font-bold uppercase leading-tight">
                        {car?.carTitle || "Fahrzeug"}
                      </h2>
                      {car?.carIdentificationNumber && (
                        <span className="text-[13px] text-ao-muted">
                          ID {car.carIdentificationNumber}
                        </span>
                      )}
                    </div>

                    {specs.length > 0 && (
                      <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                        {specs.map((s, i) => (
                          <li
                            key={i}
                            className="flex min-w-0 items-center gap-2 text-sm text-ao-soft"
                          >
                            <span className="shrink-0 text-ao-accent-fg">
                              {s.icon}
                            </span>
                            <span className="truncate">{s.value}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="mt-auto flex items-center justify-between border-t border-ao-line pt-4">
                      {price ? (
                        <span className="font-display text-3xl font-extrabold">
                          {price}
                        </span>
                      ) : (
                        <span className="text-[15px] text-ao-muted">
                          Preis auf Anfrage
                        </span>
                      )}
                      <span className="flex items-center gap-2 text-[15px] font-bold text-ao-accent-fg">
                        Details
                        <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-ao-line-strong px-6 py-16 text-center">
            <FaCar className="text-5xl text-ao-line-strong" />
            {!mounted ? (
              <p className="text-[15px] text-ao-muted">
                Fahrzeuge werden geladen …
              </p>
            ) : cars.length === 0 ? (
              <>
                <p className="text-lg font-semibold">
                  Aktuell sind keine Fahrzeuge im Bestand.
                </p>
                <p className="max-w-md text-[15px] text-ao-muted">
                  Schauen Sie bald wieder vorbei – oder fragen Sie uns direkt
                  nach Ihrem Wunschfahrzeug.
                </p>
              </>
            ) : (
              <>
                <p className="text-lg font-semibold">
                  Keine passenden Fahrzeuge gefunden.
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-[15px] font-bold text-ao-accent-fg hover:underline"
                >
                  Filter zurücksetzen
                </button>
              </>
            )}
          </div>
        )}

        {/* Auto verkaufen */}
        <div className="flex flex-col justify-between gap-6 rounded-2xl border border-ao-line bg-ao-surface p-6 lg:flex-row lg:items-center lg:p-8">
          <div className="flex flex-col gap-2">
            <span className="font-display text-3xl font-extrabold uppercase leading-none">
              Auto verkaufen?
            </span>
            <p className="text-[15px] text-ao-muted">
              Wir kaufen auch Ihr Fahrzeug an. Melden Sie sich einfach - wir
              machen Ihnen ein Angebot.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:flex">
            <a
              href="tel:+4915158124394"
              className="flex h-12 items-center justify-center gap-2 rounded-xl bg-ao-accent px-6 font-bold text-ao-on-accent transition-colors hover:bg-ao-accent-hover"
            >
              <FaPhone className="text-sm" /> Anrufen
            </a>
            <a
              href="mailto:autoservice.aundo@gmail.com?subject=Fahrzeug%20verkaufen"
              className="flex h-12 items-center justify-center gap-2 rounded-xl border border-ao-line-strong px-6 font-semibold transition-colors hover:border-ao-accent"
            >
              <FaEnvelope className="text-sm" /> E-Mail
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Page;
